import { Component, OnInit, Inject } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { FilterDisplaySettings } from '../brand-filter-container.service';
import { BrandsService } from '../../brands.service';
import { Group } from '../../brands.api.service';

export interface GroupData {
  selectedGroup: GroupWithDisplayName;
  filterSettings: FilterDisplaySettings;
}

export class GroupWithDisplayName {
  constructor(
    public name: string,
    public group: Group,
  ) {}
}

@Component({
  selector: 'bc-brand-group-dialog',
  templateUrl: './brand-group-dialog.component.html',
  styleUrls: ['./brand-group-dialog.component.scss'],
  standalone: false,
})
export class BrandGroupDialogComponent implements OnInit {
  groupControl = new UntypedFormControl();

  filteredOptions$: Observable<GroupWithDisplayName[]>;

  selectedGroup: GroupWithDisplayName;
  options$: Observable<GroupWithDisplayName[]>;

  constructor(
    public dialogRef: MatDialogRef<BrandGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GroupData,
    private brandsService: BrandsService,
  ) {
    this.selectedGroup = data.selectedGroup;
    this.options$ = this.brandsService.allGroups$.pipe(
      map((groups) => {
        if (!groups) {
          return [];
        }
        const paths = new Map<string, Group>();
        groups.forEach((g) => {
          paths.set(g.pathNodes.join('|'), g);
        });

        return groups
          .map((g) => {
            return new GroupWithDisplayName(this.buildGroupName(paths, g), g);
          })
          .sort((a, b) => a.name.localeCompare(b.name));
      }),
    );
  }

  buildGroupName(groupsByPath: Map<string, Group>, group: Group): string {
    const parent = groupsByPath.get(group.pathNodes.slice(0, -1).join('|'));
    if (!parent) {
      return group.name;
    } else {
      const parentName = this.buildGroupName(groupsByPath, parent);
      return `${parentName} / ${group.name}`;
    }
  }

  ngOnInit(): void {
    this.filteredOptions$ = combineLatest([this.groupControl.valueChanges.pipe(startWith('')), this.options$]).pipe(
      map(([value, options]) => this._filter(value, options)),
    );
  }

  private _filter(value: string, options: GroupWithDisplayName[]): GroupWithDisplayName[] {
    const filterValue = value.toLowerCase();

    return options.filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  close(newData: boolean): void {
    if (!newData) {
      this.dialogRef.close();
      return;
    }
    this.dialogRef.close({
      selectedGroup: this.selectedGroup,
    });
  }

  setGroupOpt(event: MatAutocompleteSelectedEvent): void {
    // Find the selected option
    this.filteredOptions$
      .pipe(
        take(1),
        map((options) => {
          for (const option of options) {
            if (option.name === event.option.value) {
              return option;
            }
          }
        }),
      )
      .subscribe((option) => {
        this.setGroup(option);
      });
  }

  setGroup(id: any): void {
    this.selectedGroup = id;
  }
}
